"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

export default function ContactInteractive() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Backend connection point: POST to /api/contact with formData
    setSubmitted(true);
  };

  return (
    <div className="container-main pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-white/30 mb-10">
        <Link href="/homepage" className="hover:text-white/60 transition-colors">首頁</Link>
        <span>/</span>
        <span className="text-gold-DEFAULT">聯絡我們</span>
      </div>

      {/* Page title */}
      <div className="text-center mb-14">
        <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">聯絡我們</span>
        <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
          開始合作
        </h1>
        <p className="text-white/50 max-w-xl mx-auto">
          無論是產品查詢、批發報價還是合作洽談，我們的團隊隨時為您服務
        </p>
      </div>

      {/* WhatsApp prominent CTA */}
      <div className="glass-card-gold rounded-2xl p-8 md:p-10 text-center mb-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-32 blur-[60px] opacity-20"
            style={{ background: "radial-gradient(ellipse, #25D366 0%, transparent 70%)" }}
          />
        </div>
        <div className="relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-green-500/15 border border-green-500/25 flex items-center justify-center mx-auto mb-5">
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current text-green-400">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </div>
          <h2 className="font-serif text-2xl font-bold text-white mb-2">WhatsApp 即時查詢</h2>
          <p className="text-white/50 text-sm mb-6">最快捷的聯絡方式，即時獲得回覆</p>
          <a
            href="https://wa.me/85294927816?text=你好，我想查詢信悅日用品的產品及報價資訊"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn inline-flex items-center gap-3 px-10 py-4 rounded-xl text-white font-bold text-lg"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            852 9492 7816
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Contact form */}
        <div className="lg:col-span-3">
          <div className="glass-card rounded-2xl p-8">
            <h2 className="font-serif text-xl font-bold text-white mb-6">發送查詢</h2>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckIcon" size={28} className="text-green-400" />
                </div>
                <h3 className="font-serif text-xl text-white font-bold mb-2">查詢已發送！</h3>
                <p className="text-white/50 text-sm">我們將在 24 小時內回覆您的查詢</p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
                  }}
                  className="mt-6 btn-gold-outline px-6 py-2 rounded-lg text-sm"
                >
                  再次發送
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="您的姓名"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">公司名稱</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="公司名稱（選填）"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">電郵地址 *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">電話號碼</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+852 XXXX XXXX"
                      className="form-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-white/40 block mb-1.5">查詢類別 *</label>
                  <select
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">請選擇查詢類別</option>
                    <option value="wholesale">批發採購查詢</option>
                    <option value="product">產品資訊查詢</option>
                    <option value="pricing">報價查詢</option>
                    <option value="partnership">合作洽談</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-white/40 block mb-1.5">查詢內容 *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="請詳細描述您的查詢或需求..."
                    className="form-input resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-3.5 rounded-xl text-white font-bold text-sm"
                >
                  發送查詢
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Contact info sidebar */}
        <div className="lg:col-span-2 space-y-5">
          {/* HK Office */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🇭🇰</span>
              <div>
                <p className="text-white font-semibold font-serif">香港辦事處</p>
                <p className="text-xs text-white/35">Hong Kong Office</p>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Icon name="MapPinIcon" size={16} className="text-gold-DEFAULT mt-0.5 flex-shrink-0" />
                <p className="text-white/60 leading-relaxed">
                  香港 新界葵涌<br />
                  貴盛工業大廈一期
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="PhoneIcon" size={16} className="text-green-400 flex-shrink-0" />
                <a
                  href="https://wa.me/85294927816"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-300 transition-colors"
                >
                  WhatsApp: 852 9492 7816
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="ClockIcon" size={16} className="text-white/30 flex-shrink-0" />
                <p className="text-white/50">週一至週六 9:00–18:00</p>
              </div>
            </div>
          </div>

          {/* Shanghai HQ */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🇨🇳</span>
              <div>
                <p className="text-white font-semibold font-serif">上海總公司</p>
                <p className="text-xs text-white/35">Shanghai Headquarters</p>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Icon name="MapPinIcon" size={16} className="text-gold-DEFAULT mt-0.5 flex-shrink-0" />
                <p className="text-white/60 leading-relaxed">
                  中國上海市<br />
                  總公司業務中心
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="ClockIcon" size={16} className="text-white/30 flex-shrink-0" />
                <p className="text-white/50">週一至週五 9:00–17:30</p>
              </div>
            </div>
          </div>

          {/* Business hours note */}
          <div className="glass-card-gold rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-green-400 blink flex-shrink-0" />
              <p className="text-sm text-white font-semibold">業務運營中</p>
            </div>
            <p className="text-xs text-white/50 leading-relaxed">
              WhatsApp 查詢通常在 2 小時內回覆。如需緊急協助，請直接致電或發送 WhatsApp 訊息。
            </p>
          </div>

          {/* Quick links */}
          <div className="glass-card rounded-xl p-5">
            <p className="text-xs text-white/30 uppercase tracking-widest font-semibold mb-3">快速連結</p>
            <div className="space-y-2">
              <Link
                href="/products"
                className="flex items-center justify-between text-sm text-white/60 hover:text-white transition-colors py-1.5 group"
              >
                <span>瀏覽產品目錄</span>
                <Icon name="ArrowRightIcon" size={14} className="text-white/30 group-hover:text-gold-DEFAULT transition-colors" />
              </Link>
              <Link
                href="/about"
                className="flex items-center justify-between text-sm text-white/60 hover:text-white transition-colors py-1.5 group"
              >
                <span>了解公司背景</span>
                <Icon name="ArrowRightIcon" size={14} className="text-white/30 group-hover:text-gold-DEFAULT transition-colors" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Map placeholder */}
      <div className="mt-12">
        <div className="section-divider mb-10" />
        <h2 className="font-serif text-2xl font-bold text-white mb-6">辦事處位置</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* HK map placeholder */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div
              className="h-56 relative flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #1E1E2A 0%, #16161F 100%)",
              }}
            >
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-3">
                  <Icon name="MapPinIcon" size={22} className="text-primary-light" />
                </div>
                <p className="text-white font-serif font-semibold text-sm">香港辦事處</p>
                <p className="text-white/40 text-xs mt-1">新界葵涌貴盛工業大廈一期</p>
                <a
                  href="https://maps.google.com/?q=貴盛工業大廈,葵涌,香港"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-gold-DEFAULT hover:text-gold-light transition-colors"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                  在 Google Maps 中查看
                </a>
              </div>
              {/* Decorative grid */}
              <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: "linear-gradient(rgba(212,168,67,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,168,67,0.3) 1px, transparent 1px)",
                  backgroundSize: "30px 30px",
                }}
              />
            </div>
            <div className="p-4 border-t border-white/[0.06]">
              <p className="text-xs text-white/40">🇭🇰 香港 新界葵涌 貴盛工業大廈一期</p>
            </div>
          </div>

          {/* Shanghai map placeholder */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div
              className="h-56 relative flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #1E1E2A 0%, #16161F 100%)",
              }}
            >
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-gold-DEFAULT/20 border border-gold-DEFAULT/30 flex items-center justify-center mx-auto mb-3">
                  <Icon name="MapPinIcon" size={22} className="text-gold-DEFAULT" />
                </div>
                <p className="text-white font-serif font-semibold text-sm">上海總公司</p>
                <p className="text-white/40 text-xs mt-1">Shanghai Headquarters</p>
                <a
                  href="https://maps.google.com/?q=Shanghai,China"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-gold-DEFAULT hover:text-gold-light transition-colors"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                  在 Google Maps 中查看
                </a>
              </div>
              <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: "linear-gradient(rgba(196,30,58,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(196,30,58,0.3) 1px, transparent 1px)",
                  backgroundSize: "30px 30px",
                }}
              />
            </div>
            <div className="p-4 border-t border-white/[0.06]">
              <p className="text-xs text-white/40">🇨🇳 中國 上海市 總公司業務中心</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}